/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { useAnalytics } from '@/hooks/use-analytics';
import type { CategorySuggestion } from './types';

interface SuggestionsCategoryListProps {
    suggestions?: CategorySuggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
}

const SuggestionsCategoryList: React.FC<SuggestionsCategoryListProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
}) => {
    const analytics = useAnalytics();

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: CategorySuggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            {suggestions.map((suggestion) => (
                <button
                    key={suggestion.link}
                    type="button"
                    data-slot="suggestion"
                    onMouseDown={() => handleClick(suggestion)}
                    className="w-full text-left hover:bg-accent hover:text-foreground transition-colors">
                    <span className="font-lato text-sm leading-5 font-semibold tracking-normal text-[#51575C]">
                        {suggestion.name}
                    </span>
                    {suggestion.parentCategoryName && (
                        <span className="font-lato text-sm leading-5 font-normal tracking-normal text-[#9D9D9D]">
                            {' '}
                            {`in ${suggestion.parentCategoryName}`}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
};

export default SuggestionsCategoryList;
